import React, { useMemo, useState } from "react";
import logoo from "../assets/logo.svg";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";

function Header({ onOpenAuth, onOpenMenu }) {
  const navigate = useNavigate();
  const { search, pathname } = useLocation();
  const params = new URLSearchParams(search);
  const initial = params.get("q") || "";
  const [value, setValue] = useState(initial);
  const token = useSelector((state) => state.auth?.token);
  const isLoggedIn = Boolean(token);

  const [isDark, setIsDark] = useState(() => {
    try {
      const stored = localStorage.getItem("femtyz_theme");
      if (stored === "dark") return true;
      if (stored === "light") return false;
      return window.matchMedia?.("(prefers-color-scheme: dark)")?.matches ?? false;
    } catch {
      return false;
    }
  });

  const hamburgerIcon = useMemo(
    () => (
      <svg
        viewBox="0 0 24 24"
        className="h-5 w-5"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      >
        <path d="M4 7h16" />
        <path d="M4 12h16" />
        <path d="M4 17h16" />
      </svg>
    ),
    []
  );

  // UX: if user clears the search box on the search page, go back home immediately.
  React.useEffect(() => {
    if (pathname === "/search" && !value.trim()) navigate("/");
  }, [pathname, value, navigate]);

  React.useEffect(() => {
    const root = document.documentElement;
    if (isDark) root.classList.add("dark");
    else root.classList.remove("dark");
    try {
      localStorage.setItem("femtyz_theme", isDark ? "dark" : "light");
    } catch {
      // ignore
    }
  }, [isDark]);

  function submitSearch(e) {
    e?.preventDefault();
    const q = value.trim();
    if (!q) {
      navigate("/");
      return;
    }
    navigate(`/search?q=${encodeURIComponent(q)}`);
  }

  return (
    <header className="sticky top-0 z-20 border-b border-slate-200 bg-[#F5F7FB]/85 backdrop-blur dark:border-slate-800 dark:bg-slate-950/75">
      <div className="mx-auto flex max-w-8xl items-center gap-3 px-4 py-3 md:px-6">
        <div className="flex items-center gap-2">

          <Link to="/" className="flex items-center gap-2">
            <img src={logoo} className="h-9 w-auto" alt="logo" />
          </Link>
        </div>

        <form onSubmit={submitSearch} className="flex flex-1 items-center justify-center">
          <div className="flex w-full max-w-[560px] items-center gap-2 rounded-full border border-slate-200 bg-white/70 px-3 py-2 shadow-sm dark:border-slate-800 dark:bg-slate-900/70 md:px-4">
            <input
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder="Paste a list or search (e.g., Milk, Eggs, Bread)..."
              className="w-full bg-transparent text-[13px] text-[#24303A] placeholder:text-[#6E7B86] focus:outline-none dark:text-slate-100 dark:placeholder:text-slate-400"
            />
            <button
              type="submit"
              className="hidden rounded-full bg-[#7352C7] px-4 py-1.5 text-xs font-semibold text-white hover:bg-[#6546c3] md:block"
            >
              Search
            </button>
            <button
              type="submit"
              aria-label="Search"
              className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-[#7352C7] text-white hover:bg-[#6546c3] md:hidden"
            >
              <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <path d="M21 21l-4.35-4.35" />
                <circle cx="11" cy="11" r="7" />
              </svg>
            </button>
          </div>
        </form>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setIsDark((v) => !v)}
            aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
            className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-white shadow-sm ring-1 ring-slate-200 text-slate-700 dark:bg-slate-900 dark:ring-slate-800 dark:text-slate-100"
          >
            {isDark ? (
              <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <path d="M12 4V2" />
                <path d="M12 22v-2" />
                <path d="M4.22 4.22l1.42 1.42" />
                <path d="M18.36 18.36l1.42 1.42" />
                <path d="M2 12h2" />
                <path d="M20 12h2" />
                <path d="M4.22 19.78l1.42-1.42" />
                <path d="M18.36 5.64l1.42-1.42" />
                <circle cx="12" cy="12" r="4" />
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <path d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z" />
              </svg>
            )}
          </button>

        
          {isLoggedIn ? (
            <button
              type="button"
              aria-label="Profile"
              className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-white shadow-sm ring-1 ring-slate-200 text-slate-700 dark:bg-slate-900 dark:ring-slate-800 dark:text-slate-100"
              onClick={() => navigate("/settings")}
            >
              <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <circle cx="12" cy="7" r="4" />
                <path d="M20 21a8 8 0 10-16 0" />
              </svg>
            </button>
          ) : (
            <>
              <button
                type="button"
                className="hidden rounded-full border border-slate-200 bg-white px-4 py-2 text-xs font-semibold text-slate-800 hover:border-indigo-500 hover:text-indigo-600 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-100 md:inline-flex"
                onClick={() => onOpenAuth?.("login")}
              >
                Sign in
              </button>
              <button
                type="button"
                className="hidden rounded-full bg-[#7352C7] px-4 py-2 text-xs font-semibold text-white hover:bg-[#6546c3] md:inline-flex"
                onClick={() => onOpenAuth?.("signup")}
              >
                Sign up
              </button>
              <button
                type="button"
                aria-label="Sign in"
                className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-white shadow-sm ring-1 ring-slate-200 text-slate-700 dark:bg-slate-900 dark:ring-slate-800 dark:text-slate-100 md:hidden"
                onClick={() => onOpenAuth?.("login")}
              >
                <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <circle cx="12" cy="7" r="4" />
                  <path d="M20 21a8 8 0 10-16 0" />
                </svg>
              </button>
            </>
          )}
            <button
            type="button"
            onClick={onOpenMenu}
            aria-label="Open menu"
            className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-white shadow-sm ring-1 ring-slate-200 text-slate-700 dark:bg-slate-900 dark:ring-slate-800 dark:text-slate-100"
          >
            {hamburgerIcon}
          </button>

        </div>
      </div>
    </header>
  );
}

export default Header;